import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth, api } from "@/context/AuthContext";
import MaterialAutocomplete from "@/components/MaterialAutocomplete";
import MaterialUsageHistory from "@/components/MaterialUsageHistory";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Plus, Pencil, Trash2, Search, ClipboardList, Car, Users, Clock, Package, 
  CheckCircle2, X, RefreshCw, Mic, MicOff, Eye, Archive, UserCog, Wrench
} from "lucide-react";
import OrderDetailSheet from "@/components/OrderDetailSheet";
import VoiceInputButton from "@/components/VoiceInputButton";
import OcrScanner from "@/components/OcrScanner";
import { OrderCard, TaskFormDialog, ManualTimeDialog, QuickAddMaterialDialog, MaterialFormFields, emptyMaterial, computeMaterialCost, materialFromInventory, materialEinheiten, calculateTotals, formatMinutes } from "@/components/orders";

const WorkOrdersPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("alle");
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  
  // Order Detail Sheet state
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [detailOrder, setDetailOrder] = useState(null);
  
  // Speech recognition state
  const [isListening, setIsListening] = useState(false);
  const [activeField, setActiveField] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editTaskDialogOpen, setEditTaskDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  
  // Manual time entry (Nachtrag) state
  const [manualTimeOpen, setManualTimeOpen] = useState(false);
  const [manualTimeOrderId, setManualTimeOrderId] = useState(null);
  const [manualTimeForm, setManualTimeForm] = useState({
    date: new Date().toISOString().split("T")[0],
    start_time: "08:00",
    end_time: "16:00",
    pause_minutes: 30,
    notes: ""
  });
  
  // Inline create modes
  const [createNewCustomer, setCreateNewCustomer] = useState(false);
  const [createNewVehicle, setCreateNewVehicle] = useState(false);
  
  // New customer data (for inline creation)
  const [newCustomerData, setNewCustomerData] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    street: "",
    postal_code: "",
    city: ""
  });
  
  // New vehicle data (for inline creation)
  const [newVehicleData, setNewVehicleData] = useState({
    license_plate: "",
    brand: "",
    model: "",
    year: "",
    vin: "",
    mileage: ""
  });
  
  const [formData, setFormData] = useState({
    customer_id: "",
    vehicle_id: "",
    description: "",
    auftragsnummer: "",
    status: "offen",
    assigned_to: [],
    service_object: "",
    no_vehicle: false, // Toggle: "Kein Fahrzeug (nur Dienstleistung)"
  });

  // Editable customer data (for edit mode)
  const [customerData, setCustomerData] = useState({
    name: "",
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    street: "",
    postal_code: "",
    city: ""
  });

  // Editable vehicle data (for edit mode)
  const [vehicleData, setVehicleData] = useState({
    license_plate: "",
    brand: "",
    model: "",
    year: "",
    vin: "",
    mileage: ""
  });

  // Tasks for the order being edited
  const [editTasks, setEditTasks] = useState([]);

  // Tasks for new order
  const [newTasks, setNewTasks] = useState([]);
  const [currentTask, setCurrentTask] = useState({
    description: "",
    materialien: [],
    time_minutes: 0
  });
  const [currentMaterial, setCurrentMaterial] = useState({
    bezeichnung: "",
    anzahl: "",
    einheit: "Stück",
    artikelnummer: "",
    einzelpreis: 0,
    einkaufspreis: 0,
    marge_prozent: 0,
    kosten: 0,
    material_id: "",
    flaeche_laenge: "",
    flaeche_breite: "",
    flaeche_einheit: "cm"
  });
  
  const [customerVehicles, setCustomerVehicles] = useState([]);
  const [mitarbeiterList, setMitarbeiterList] = useState([]);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [pendingMaterialForLager, setPendingMaterialForLager] = useState(null);
  const [pendingMaterialForLagerMode, setPendingMaterialForLagerMode] = useState(null); // 'order_specific' | 'lager'
  const [viewMode, setViewMode] = useState("active"); // 'active' or 'archive'

  const fetchData = async (mode = viewMode) => {
    setLoading(true);
    setError(null);
    try {
      const archivedParam = mode === "archive" ? "?archived=true" : "";
      const [ordersRes, customersRes, vehiclesRes] = await Promise.all([
        api.get(`/api/work-orders${archivedParam}`),
        api.get("/api/customers"),
        api.get("/api/vehicles")
      ]);
      setOrders(ordersRes.data);
      setCustomers(customersRes.data);
      setVehicles(vehiclesRes.data);
      // Load mitarbeiter list for assignment (only for admin)
      if (user?.role !== "mitarbeiter") {
        try {
          const maRes = await api.get("/api/mitarbeiter");
          setMitarbeiterList(maRes.data);
        } catch (e) { /* ignore if no mitarbeiter */ }
      }
    } catch (err) {
      console.error("Work orders fetch error:", err);
      setError("Fehler beim Laden der Daten");
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // State for highlighting an order from URL param
  const [highlightedOrderId, setHighlightedOrderId] = useState(null);

  // Handle URL parameters (status filter, order selection, new order)
  useEffect(() => {
    const statusParam = searchParams.get("status");
    const orderIdParam = searchParams.get("order");
    const newOrderParam = searchParams.get("new");
    
    // Open new order dialog immediately (doesn't need orders to be loaded)
    if (newOrderParam === "true") {
      setDialogOpen(true);
      setSearchParams({}, { replace: true });
      return;
    }
    
    // For other params, wait for orders to load
    if (loading || orders.length === 0) return;
    
    // Set status filter from URL
    if (statusParam && ["offen", "in_bearbeitung", "erledigt"].includes(statusParam)) {
      setStatusFilter(statusParam);
    }
    
    // Open specific order in detail sheet from URL
    if (orderIdParam) {
      const orderToOpen = orders.find(o => o.id === orderIdParam);
      if (orderToOpen) {
        // Open the detail sheet instead of just highlighting
        setDetailOrder(orderToOpen);
        setDetailSheetOpen(true);
      }
    }
    
    // Clear URL parameters after processing
    if (statusParam || orderIdParam) {
      setSearchParams({}, { replace: true });
    }
  }, [loading, orders, searchParams, setSearchParams]);

  // Function to open order detail
  const openOrderDetail = (order) => {
    setDetailOrder(order);
    setDetailSheetOpen(true);
  };

  // Function to handle order update from detail sheet
  const handleOrderUpdateFromSheet = (updatedOrder) => {
    setOrders(prevOrders => 
      prevOrders.map(o => o.id === updatedOrder.id ? updatedOrder : o)
    );
    setDetailOrder(updatedOrder);
  };

  // Speech Recognition Setup
  const startListening = (fieldType, setter, currentValue = "") => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error("Spracheingabe wird von diesem Browser nicht unterstützt");
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.lang = 'de-DE';
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onstart = () => {
      setIsListening(true);
      setActiveField(fieldType);
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      // Append to existing value or replace
      const newValue = currentValue ? `${currentValue} ${transcript}` : transcript;
      setter(newValue);
      toast.success(`Erkannt: "${transcript}"`);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      if (event.error === 'no-speech') {
        toast.error("Keine Sprache erkannt. Bitte erneut versuchen.");
      } else {
        toast.error(`Fehler: ${event.error}`);
      }
    };

    recognition.onend = () => {
      setIsListening(false);
      setActiveField(null);
    };

    recognition.start();
  };

  // Voice input for task description
  const voiceTaskDescription = () => {
    startListening('taskDescription', (val) => {
      setCurrentTask(prev => ({ ...prev, description: val }));
    }, currentTask.description);
  };

  // Voice input for material fields (new order form only)
  const voiceMaterialBezeichnung = () => {
    startListening('materialBezeichnung', (val) => {
      setCurrentMaterial(prev => ({ ...prev, bezeichnung: val, material_id: "" }));
    }, currentMaterial.bezeichnung);
  };

  const handleCustomerChange = (customerId) => {
    setFormData({ ...formData, customer_id: customerId, vehicle_id: "" });
    const filteredVehicles = vehicles.filter(v => v.customer_id === customerId);
    setCustomerVehicles(filteredVehicles);
  };

  // Index for editing material in current task (-1 means adding new)
  const [editingCurrentMaterialIndex, setEditingCurrentMaterialIndex] = useState(-1);

  const addMaterialToCurrentTask = () => {
    if (!currentMaterial.bezeichnung.trim()) {
      toast.error("Bitte Materialbezeichnung eingeben");
      return;
    }
    const materialToSave = computeMaterialCost(currentMaterial);
    if (editingCurrentMaterialIndex >= 0) {
      const updatedMaterialien = [...currentTask.materialien];
      updatedMaterialien[editingCurrentMaterialIndex] = materialToSave;
      setCurrentTask({ ...currentTask, materialien: updatedMaterialien });
      setEditingCurrentMaterialIndex(-1);
      // Also offer to add to inventory if user replaced the material with a new one
      if (user?.role === "admin" && !materialToSave.material_id) {
        setPendingMaterialForLager(materialToSave);
      }
    } else {
      setCurrentTask({ ...currentTask, materialien: [...currentTask.materialien, materialToSave] });
      // Admin: offer to add to inventory if not from autocomplete
      if (user?.role === "admin" && !materialToSave.material_id) {
        setPendingMaterialForLager(materialToSave);
      }
    }
    setCurrentMaterial(emptyMaterial());
  };
  
  const editMaterialInCurrentTask = (index) => {
    setCurrentMaterial({ ...currentTask.materialien[index] });
    setEditingCurrentMaterialIndex(index);
  };
  
  const cancelCurrentMaterialEdit = () => {
    setEditingCurrentMaterialIndex(-1);
    setCurrentMaterial(emptyMaterial());
  };

  const removeMaterialFromCurrentTask = (index) => {
    setCurrentTask({ ...currentTask, materialien: currentTask.materialien.filter((_, i) => i !== index) });
    if (editingCurrentMaterialIndex === index) cancelCurrentMaterialEdit();
  };

  // ID of task currently being edited (from newTasks). null = adding new.
  const [editingNewTaskId, setEditingNewTaskId] = useState(null);

  const addTaskToNewOrder = () => {
    if (!currentTask.description.trim()) {
      toast.error("Bitte Aufgabenbeschreibung eingeben");
      return;
    }
    if (editingNewTaskId) {
      // Update existing pending task
      setNewTasks(newTasks.map(t =>
        t.id === editingNewTaskId ? { ...currentTask, id: editingNewTaskId } : t
      ));
      setEditingNewTaskId(null);
      toast.success("Aufgabe aktualisiert");
    } else {
      setNewTasks([...newTasks, { ...currentTask, id: Date.now().toString() }]);
    }
    setCurrentTask({ description: "", materialien: [], time_minutes: 0 });
    setEditingCurrentMaterialIndex(-1);
  };

  const editTaskInNewOrder = (taskId) => {
    const task = newTasks.find(t => t.id === taskId);
    if (!task) return;
    setCurrentTask({
      description: task.description || "",
      materialien: task.materialien ? [...task.materialien] : [],
      time_minutes: task.time_minutes || 0
    });
    setEditingNewTaskId(taskId);
    setEditingCurrentMaterialIndex(-1);
    setCurrentMaterial(emptyMaterial());
    // Scroll to the edit form so it's immediately visible
    setTimeout(() => {
      const el = document.querySelector('[data-testid="new-task-description"]');
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.focus();
      }
    }, 100);
  };

  const cancelEditNewTask = () => {
    setEditingNewTaskId(null);
    setCurrentTask({ description: "", materialien: [], time_minutes: 0 });
    setEditingCurrentMaterialIndex(-1);
    setCurrentMaterial(emptyMaterial());
  };

  const removeTaskFromNewOrder = (taskId) => {
    setNewTasks(newTasks.filter(t => t.id !== taskId));
    if (editingNewTaskId === taskId) cancelEditNewTask();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!editingOrder && newTasks.length === 0) {
      toast.error("Bitte mindestens eine Aufgabe hinzufügen");
      return;
    }
    
    try {
      if (editingOrder) {
        // Update customer data
        await api.put(`/api/customers/${formData.customer_id}`, customerData);
        
        // Update vehicle data (only if the order still has a vehicle)
        if (formData.vehicle_id && !formData.no_vehicle) {
          await api.put(`/api/vehicles/${formData.vehicle_id}`, {
            ...vehicleData,
            year: parseInt(vehicleData.year) || 0,
            mileage: parseInt(vehicleData.mileage) || 0
          });
        }
        
        // Update order data
        await api.put(`/api/work-orders/${editingOrder.id}`, { 
          customer_id: formData.customer_id,
          vehicle_id: formData.no_vehicle ? null : (formData.vehicle_id || null),
          description: formData.description,
          auftragsnummer: formData.auftragsnummer,
          status: formData.status,
          assigned_to: formData.assigned_to,
          service_object: formData.service_object || "",
        });
        toast.success("Auftrag aktualisiert");
      } else {
        let customerId = formData.customer_id;
        let vehicleId = formData.vehicle_id;
        
        // Create new customer if needed
        if (createNewCustomer) {
          if (!newCustomerData.first_name.trim() || !newCustomerData.last_name.trim()) {
            toast.error("Bitte Vor- und Nachname des Kunden eingeben");
            return;
          }
          const customerRes = await api.post("/api/customers", newCustomerData);
          customerId = customerRes.data.id;
          toast.success("Neuer Kunde angelegt");
        }
        
        // Create new vehicle if needed (skip when no_vehicle toggle is on)
        if (createNewVehicle && !formData.no_vehicle) {
          if (!newVehicleData.license_plate.trim()) {
            toast.error("Bitte Kennzeichen eingeben");
            return;
          }
          const vehicleRes = await api.post("/api/vehicles", {
            ...newVehicleData,
            customer_id: customerId,
            year: parseInt(newVehicleData.year) || new Date().getFullYear(),
            mileage: parseInt(newVehicleData.mileage) || 0
          });
          vehicleId = vehicleRes.data.id;
          toast.success("Neues Fahrzeug angelegt");
        }
        
        // Validate selections
        if (!customerId) {
          toast.error("Bitte Kunde auswählen oder neu anlegen");
          return;
        }
        if (formData.no_vehicle) {
          // Objekt-/Dienstleistungs-Auftrag: no vehicle needed
          if (!(formData.service_object || "").trim()) {
            toast.error("Bitte eine Objekt- oder Dienstleistungs-Bezeichnung eingeben");
            return;
          }
          vehicleId = null;
        } else if (!vehicleId) {
          toast.error("Bitte Fahrzeug auswählen oder 'Ohne Fahrzeug' aktivieren");
          return;
        }
        
        // Create order with tasks
        const orderData = {
          customer_id: customerId,
          vehicle_id: vehicleId,
          service_object: formData.service_object || "",
          description: formData.description,
          auftragsnummer: formData.auftragsnummer,
          assigned_to: formData.assigned_to,
          tasks: newTasks.map(t => ({
            description: t.description,
            materialien: t.materialien || [],
            time_minutes: t.time_minutes
          }))
        };
        await api.post("/api/work-orders", orderData);
        toast.success("Auftrag erstellt");
        // Stock is auto-reserved on the backend
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const handleEdit = (order) => {
    setEditingOrder(order);
    setFormData({
      customer_id: order.customer_id,
      vehicle_id: order.vehicle_id || "",
      description: order.description,
      auftragsnummer: order.auftragsnummer || "",
      status: order.status,
      assigned_to: order.assigned_to || [],
      service_object: order.service_object || "",
      no_vehicle: !order.vehicle_id,
    });
    
    // Load customer data
    const customer = customers.find(c => c.id === order.customer_id);
    if (customer) {
      setCustomerData({
        name: customer.name || "",
        first_name: customer.first_name || "",
        last_name: customer.last_name || "",
        phone: customer.phone || "",
        email: customer.email || "",
        street: customer.street || "",
        postal_code: customer.postal_code || "",
        city: customer.city || ""
      });
    }
    
    // Load vehicle data
    const vehicle = vehicles.find(v => v.id === order.vehicle_id);
    if (vehicle) {
      setVehicleData({
        license_plate: vehicle.license_plate || "",
        brand: vehicle.brand || "",
        model: vehicle.model || "",
        year: vehicle.year?.toString() || "",
        vin: vehicle.vin || "",
        mileage: vehicle.mileage?.toString() || ""
      });
    }
    
    // Load tasks
    setEditTasks(order.tasks || []);
    
    // Load customer's vehicles
    const filteredVehicles = vehicles.filter(v => v.customer_id === order.customer_id);
    setCustomerVehicles(filteredVehicles);
    setDialogOpen(true);
  };

  const handleDelete = async (orderId) => {
    if (!window.confirm("Auftrag löschen?")) return;
    try {
      await api.delete(`/api/work-orders/${orderId}`);
      toast.success("Auftrag gelöscht");
      fetchData();
    } catch (err) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await api.put(`/api/work-orders/${orderId}`, { status: newStatus });
      toast.success("Status aktualisiert");
      fetchData();
    } catch (err) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handleArchive = async (orderId) => {
    if (!window.confirm("Diesen Auftrag ins Archiv verschieben?")) return;
    try {
      await api.post(`/api/work-orders/${orderId}/archive`);
      toast.success("Auftrag archiviert");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Archivieren fehlgeschlagen");
    }
  };

  const handleUnarchive = async (orderId) => {
    try {
      await api.post(`/api/work-orders/${orderId}/unarchive`);
      toast.success("Auftrag wiederhergestellt");
      fetchData();
    } catch {
      toast.error("Wiederherstellen fehlgeschlagen");
    }
  };

  const handleToggleTask = async (orderId, taskId, completed) => {
    try {
      await api.put(`/api/work-orders/${orderId}/tasks/${taskId}?completed=${!completed}`);
      // Update local state instead of refetching to keep accordion open
      setOrders(prevOrders => 
        prevOrders.map(order => {
          if (order.id === orderId) {
            const updatedTasks = order.tasks.map(task => 
              task.id === taskId ? { ...task, completed: !completed } : task
            );
            // Also update arbeitskarte if it exists
            let updatedArbeitskarte = order.arbeitskarte;
            if (updatedArbeitskarte && updatedArbeitskarte.arbeiten) {
              const taskIndex = order.tasks.findIndex(t => t.id === taskId);
              if (taskIndex >= 0 && taskIndex < updatedArbeitskarte.arbeiten.length) {
                updatedArbeitskarte = {
                  ...updatedArbeitskarte,
                  arbeiten: updatedArbeitskarte.arbeiten.map((a, i) => 
                    i === taskIndex ? { ...a, erledigt: !completed } : a
                  )
                };
              }
            }
            return {
              ...order,
              tasks: updatedTasks,
              arbeitskarte: updatedArbeitskarte
            };
          }
          return order;
        })
      );
    } catch (err) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  // Update Abschlusskontrolle in Arbeitskarte
  const handleAbschlusskontrolleChange = async (orderId, field, value) => {
    try {
      await api.put(`/api/work-orders/${orderId}/arbeitskarte/abschlusskontrolle`, {
        [field]: value
      });
      // Update local state
      setOrders(prevOrders => 
        prevOrders.map(order => {
          if (order.id === orderId && order.arbeitskarte) {
            return {
              ...order,
              arbeitskarte: {
                ...order.arbeitskarte,
                abschlusskontrolle: {
                  ...order.arbeitskarte.abschlusskontrolle,
                  [field]: value
                }
              }
            };
          }
          return order;
        })
      );
    } catch (err) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  // Time Tracking Functions
  const startTimer = async (orderId) => {
    try {
      await api.post(`/api/work-orders/${orderId}/time/start`);
      toast.success("Zeiterfassung gestartet");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Starten");
    }
  };

  const togglePause = async (orderId) => {
    try {
      await api.post(`/api/work-orders/${orderId}/time/pause`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  const stopTimer = async (orderId) => {
    try {
      const response = await api.post(`/api/work-orders/${orderId}/time/stop`);
      if (response.data.status === "erledigt") {
        toast.success("Zeiterfassung beendet - Auftrag als erledigt markiert!");
      } else {
        toast.success("Zeiterfassung beendet");
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Beenden");
    }
  };

  const deleteTimeEntry = async (orderId, entryId) => {
    if (!window.confirm("Zeiteintrag löschen?")) return;
    try {
      await api.delete(`/api/work-orders/${orderId}/time/${entryId}`);
      toast.success("Zeiteintrag gelöscht");
      fetchData();
    } catch (err) {
      toast.error("Fehler beim Löschen");
    }
  };

  const openManualTimeEntry = (orderId) => {
    setManualTimeOrderId(orderId);
    setManualTimeForm({
      date: new Date().toISOString().split("T")[0],
      start_time: "08:00",
      end_time: "16:00",
      pause_minutes: 30,
      notes: ""
    });
    setManualTimeOpen(true);
  };

  const submitManualTimeEntry = async () => {
    if (!manualTimeOrderId) return;
    try {
      await api.post(`/api/work-orders/${manualTimeOrderId}/time/manual`, manualTimeForm);
      toast.success("Zeiteintrag nachgetragen");
      setManualTimeOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Nachtragen");
    }
  };

  const getActiveTimeEntry = (order) => {
    return order.time_entries?.find(e => e.is_active);
  };

  const handleDeleteTask = async (orderId, taskId) => {
    try {
      await api.delete(`/api/work-orders/${orderId}/tasks/${taskId}`);
      toast.success("Aufgabe gelöscht");
      fetchData();
    } catch (err) {
      toast.error("Fehler beim Löschen");
    }
  };

  // Edit task functions
  const openEditTaskDialog = (order, task) => {
    setSelectedOrder(order);
    setEditingTask(task);
    setEditTaskDialogOpen(true);
  };

  // Toggle work item in Arbeitskarte
  const handleToggleArbeit = async (orderId, arbeitIndex, currentErledigt) => {
    try {
      // Find the order and task
      const order = orders.find(o => o.id === orderId);
      if (!order || !order.tasks || !order.tasks[arbeitIndex]) return;
      
      const taskId = order.tasks[arbeitIndex].id;
      await api.put(`/api/work-orders/${orderId}/tasks/${taskId}/full`, {
        completed: !currentErledigt
      });
      
      // Update local state
      setOrders(prevOrders => 
        prevOrders.map(o => {
          if (o.id === orderId) {
            const updatedTasks = [...o.tasks];
            updatedTasks[arbeitIndex] = { ...updatedTasks[arbeitIndex], completed: !currentErledigt };
            
            let updatedArbeitskarte = o.arbeitskarte;
            if (updatedArbeitskarte && updatedArbeitskarte.arbeiten) {
              const updatedArbeiten = [...updatedArbeitskarte.arbeiten];
              updatedArbeiten[arbeitIndex] = { ...updatedArbeiten[arbeitIndex], erledigt: !currentErledigt };
              updatedArbeitskarte = { ...updatedArbeitskarte, arbeiten: updatedArbeiten };
            }
            
            return { ...o, tasks: updatedTasks, arbeitskarte: updatedArbeitskarte };
          }
          return o;
        })
      );
    } catch (err) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const resetForm = () => {
    setFormData({ customer_id: "", vehicle_id: "", description: "", auftragsnummer: "", status: "offen", assigned_to: [], service_object: "", no_vehicle: false });
    setEditingOrder(null);
    setCustomerVehicles([]);
    setNewTasks([]);
    setCurrentTask({ description: "", materialien: [], time_minutes: 0 });
    setEditingNewTaskId(null);
    setCurrentMaterial({ bezeichnung: "", anzahl: "", einheit: "Stück", artikelnummer: "", einzelpreis: 0, einkaufspreis: 0, marge_prozent: 0, kosten: 0, material_id: "", flaeche_laenge: "", flaeche_breite: "", flaeche_einheit: "cm" });
    setEditingCurrentMaterialIndex(-1);
    setCreateNewCustomer(false);
    setCreateNewVehicle(false);
    setNewCustomerData({
      first_name: "",
      last_name: "",
      phone: "",
      email: "",
      street: "",
      postal_code: "",
      city: ""
    });
    setNewVehicleData({
      license_plate: "",
      brand: "",
      model: "",
      year: "",
      vin: "",
      mileage: ""
    });
    setCustomerData({
      name: "",
      first_name: "",
      last_name: "",
      phone: "",
      email: "",
      street: "",
      postal_code: "",
      city: ""
    });
    setVehicleData({
      license_plate: "",
      brand: "",
      model: "",
      year: "",
      vin: "",
      mileage: ""
    });
  };

  const filteredOrders = orders.filter(o => {
    const matchesSearch = 
      o.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (o.vehicle_info && o.vehicle_info.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (o.customer_name && o.customer_name.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === "alle" || o.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-sm text-muted-foreground">Laden...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <p className="text-muted-foreground">{error}</p>
        <Button onClick={fetchData} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Erneut versuchen
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="work-orders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
            {viewMode === "archive" ? "Archiv" : "Auftragsverwaltung"}
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {orders.length} {viewMode === "archive" ? "archivierte Aufträge" : "Aufträge gesamt"}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Active/Archive toggle */}
          <div className="inline-flex rounded-lg border bg-muted/30 p-1" role="tablist">
            <button
              type="button"
              onClick={() => { setViewMode("active"); fetchData("active"); }}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${viewMode === "active" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
              data-testid="view-mode-active"
            >
              Aktiv
            </button>
            <button
              type="button"
              onClick={() => { setViewMode("archive"); fetchData("archive"); }}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all gap-1.5 inline-flex items-center ${viewMode === "archive" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
              data-testid="view-mode-archive"
            >
              <Archive className="w-3.5 h-3.5" />
              Archiv
            </button>
          </div>
          {viewMode === "active" && (
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="btn-primary gap-2 w-full sm:w-auto" data-testid="add-order-button">
              <Plus className="w-4 h-4" />
              Neuer Auftrag
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" data-testid="order-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Barlow Condensed' }} className="text-xl">
                {editingOrder ? "Auftrag bearbeiten" : "Neuer Auftrag"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              {!editingOrder ? (
                <>
                  {/* Customer Selection or Creation */}
                  <div className="space-y-3 border rounded-md p-4 bg-muted/20">
                    <div className="flex items-center justify-between">
                      <Label className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Kunde *
                      </Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setCreateNewCustomer(!createNewCustomer);
                          if (!createNewCustomer) {
                            setFormData({ ...formData, customer_id: "" });
                            setCustomerVehicles([]);
                          }
                        }}
                        className="text-xs"
                      >
                        {createNewCustomer ? "Bestehenden wählen" : "+ Neu anlegen"}
                      </Button>
                    </div>
                    
                    {createNewCustomer ? (
                      <div className="grid grid-cols-2 gap-3">
                        <Input
                          placeholder="Vorname *"
                          value={newCustomerData.first_name}
                          onChange={(e) => setNewCustomerData({ ...newCustomerData, first_name: e.target.value })}
                        />
                        <Input
                          placeholder="Nachname *"
                          value={newCustomerData.last_name}
                          onChange={(e) => setNewCustomerData({ ...newCustomerData, last_name: e.target.value })}
                        />
                        <Input
                          placeholder="Telefon"
                          value={newCustomerData.phone}
                          onChange={(e) => setNewCustomerData({ ...newCustomerData, phone: e.target.value })}
                        />
                        <Input
                          placeholder="E-Mail"
                          value={newCustomerData.email}
                          onChange={(e) => setNewCustomerData({ ...newCustomerData, email: e.target.value })}
                        />
                        <Input
                          placeholder="Straße"
                          value={newCustomerData.street}
                          onChange={(e) => setNewCustomerData({ ...newCustomerData, street: e.target.value })}
                          className="col-span-2"
                        />
                        <Input
                          placeholder="PLZ"
                          value={newCustomerData.postal_code}
                          onChange={(e) => setNewCustomerData({ ...newCustomerData, postal_code: e.target.value })}
                        />
                        <Input
                          placeholder="Stadt"
                          value={newCustomerData.city}
                          onChange={(e) => setNewCustomerData({ ...newCustomerData, city: e.target.value })}
                        />
                      </div>
                    ) : (
                      <Select
                        value={formData.customer_id}
                        onValueChange={handleCustomerChange}
                      >
                        <SelectTrigger data-testid="order-customer-select">
                          <SelectValue placeholder="Kunde auswählen" />
                        </SelectTrigger>
                        <SelectContent>
                          {customers.map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  {/* Vehicle Selection or Creation OR Service Object */}
                  <div className="space-y-3 border rounded-md p-4 bg-muted/20">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <Label className="flex items-center gap-2">
                        {formData.no_vehicle ? <Wrench className="w-4 h-4" /> : <Car className="w-4 h-4" />}
                        {formData.no_vehicle ? "Objekt / Dienstleistung" : "Fahrzeug"} *
                      </Label>
                      <label className="inline-flex items-center gap-2 text-xs cursor-pointer" data-testid="toggle-no-vehicle-wrapper">
                        <input
                          type="checkbox"
                          checked={formData.no_vehicle}
                          onChange={e => {
                            const checked = e.target.checked;
                            setFormData(f => ({ ...f, no_vehicle: checked, vehicle_id: checked ? "" : f.vehicle_id }));
                            if (checked) setCreateNewVehicle(false);
                          }}
                          className="w-4 h-4"
                          data-testid="toggle-no-vehicle"
                        />
                        <span className="text-muted-foreground">Ohne Fahrzeug (nur Dienstleistung)</span>
                      </label>
                      {!formData.no_vehicle && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setCreateNewVehicle(!createNewVehicle);
                            if (!createNewVehicle) {
                              setFormData({ ...formData, vehicle_id: "" });
                            }
                          }}
                          className="text-xs"
                          disabled={!createNewCustomer && !formData.customer_id}
                        >
                          {createNewVehicle ? "Bestehendes wählen" : "+ Neu anlegen"}
                        </Button>
                      )}
                    </div>

                    {formData.no_vehicle ? (
                      <div className="space-y-2">
                        <Input
                          placeholder="z.B. Kabelbaum VW T4, Anhänger Wisont, Steuergerät A6..."
                          value={formData.service_object}
                          onChange={e => setFormData({ ...formData, service_object: e.target.value })}
                          data-testid="service-object-input"
                          autoFocus
                        />
                        <p className="text-xs text-muted-foreground">
                          Für Aufträge ohne Fahrzeugbezug (Vormontage, Kabelbaum-Instandsetzung, Anhänger ohne VIN, etc.).
                        </p>
                      </div>
                    ) : createNewVehicle ? (
                      <div className="grid grid-cols-2 gap-3">
                        <div className="flex gap-1">
                          <Input
                            placeholder="Kennzeichen *"
                            value={newVehicleData.license_plate}
                            onChange={(e) => setNewVehicleData({ ...newVehicleData, license_plate: e.target.value })}
                            className="flex-1"
                          />
                          <OcrScanner 
                            ocrType="license_plate" 
                            onResult={(text) => setNewVehicleData({ ...newVehicleData, license_plate: text })}
                          />
                        </div>
                        <Input
                          placeholder="Hersteller"
                          value={newVehicleData.brand}
                          onChange={(e) => setNewVehicleData({ ...newVehicleData, brand: e.target.value })}
                        />
                        <Input
                          placeholder="Modell"
                          value={newVehicleData.model}
                          onChange={(e) => setNewVehicleData({ ...newVehicleData, model: e.target.value })}
                        />
                        <Input
                          type="number"
                          placeholder="Baujahr"
                          value={newVehicleData.year}
                          onChange={(e) => setNewVehicleData({ ...newVehicleData, year: e.target.value })}
                        />
                        <div className="flex gap-1">
                          <Input
                            placeholder="Fahrgestellnr. (VIN)"
                            value={newVehicleData.vin}
                            onChange={(e) => setNewVehicleData({ ...newVehicleData, vin: e.target.value })}
                            className="flex-1"
                          />
                          <OcrScanner 
                            ocrType="vin" 
                            onResult={(text) => setNewVehicleData({ ...newVehicleData, vin: text })}
                          />
                        </div>
                        <div className="flex gap-1">
                          <Input
                            type="number"
                            placeholder="Kilometerstand"
                            value={newVehicleData.mileage}
                            onChange={(e) => setNewVehicleData({ ...newVehicleData, mileage: e.target.value })}
                            className="flex-1"
                          />
                          <OcrScanner 
                            ocrType="mileage" 
                            onResult={(text) => setNewVehicleData({ ...newVehicleData, mileage: text.replace(/\D/g, '') })}
                          />
                        </div>
                      </div>
                    ) : (
                      <Select
                        value={formData.vehicle_id}
                        onValueChange={(value) => setFormData({ ...formData, vehicle_id: value })}
                        disabled={!formData.customer_id}
                      >
                        <SelectTrigger data-testid="order-vehicle-select">
                          <SelectValue placeholder={formData.customer_id ? "Fahrzeug auswählen" : "Erst Kunde wählen"} />
                        </SelectTrigger>
                        <SelectContent>
                          {customerVehicles.map((v) => (
                            <SelectItem key={v.id} value={v.id}>
                              {v.brand} {v.model} ({v.license_plate})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  {/* General Description */}
                  <div className="space-y-2">
                    <Label>Auftragsbeschreibung</Label>
                    <div className="flex gap-2 items-center">
                      <Input
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="z.B. Inspektion, Reparatur..."
                        data-testid="order-description-input"
                        className="flex-1"
                      />
                      <VoiceInputButton onResult={(t) => setFormData((f) => ({ ...f, description: f.description ? `${f.description} ${t}` : t }))} />
                    </div>
                  </div>

                  {/* Mitarbeiter-Zuweisung */}
                  {mitarbeiterList.length > 0 && (
                    <div className="space-y-2 border rounded-md p-4 bg-muted/20">
                      <Label className="flex items-center gap-2">
                        <UserCog className="w-4 h-4" />
                        Mitarbeiter zuweisen
                      </Label>
                      <div className="space-y-2">
                        {mitarbeiterList.map((ma) => (
                          <label key={ma.id} className="flex items-center gap-2 cursor-pointer text-sm" data-testid={`assign-ma-${ma.id}`}>
                            <Checkbox
                              checked={formData.assigned_to.includes(ma.id)}
                              onCheckedChange={(checked) => {
                                setFormData(prev => ({
                                  ...prev,
                                  assigned_to: checked
                                    ? [...prev.assigned_to, ma.id]
                                    : prev.assigned_to.filter(id => id !== ma.id)
                                }));
                              }}
                            />
                            <span>{ma.name}</span>
                            <span className="text-muted-foreground text-xs">({ma.email})</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Tasks Section */}
                  <div className="border rounded-md p-4 space-y-4 bg-muted/30">
                    <h3 className="font-semibold flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                      <ClipboardList className="w-4 h-4" />
                      Aufgaben *
                    </h3>
                    
                    {/* Added Tasks */}
                    {newTasks.length > 0 && (
                      <div className="space-y-2">
                        {newTasks.map((task) => {
                          const isBeingEdited = editingNewTaskId === task.id;
                          return (
                          <div
                            key={task.id}
                            className={`p-3 rounded border ${isBeingEdited ? "bg-blue-50 border-blue-400 ring-2 ring-blue-200" : "bg-white"}`}
                            data-testid={`pending-task-${task.id}`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm">
                                  {task.description}
                                  {isBeingEdited && (
                                    <span className="ml-2 text-xs text-blue-600 font-normal">(wird bearbeitet)</span>
                                  )}
                                </p>
                                {task.time_minutes > 0 && (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    <Clock className="w-3 h-3 inline mr-1" />
                                    {task.time_minutes} min
                                  </p>
                                )}
                                {task.materialien && task.materialien.length > 0 && (
                                  <div className="mt-2 space-y-1">
                                    {task.materialien.map((m, idx) => (
                                      <div key={idx} className="text-xs bg-slate-100 p-1.5 rounded flex items-center gap-2">
                                        <Package className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                                        <span className="font-medium">{m.bezeichnung}</span>
                                        {m.menge && <span className="text-muted-foreground">({m.menge})</span>}
                                        {m.artikelnummer && <span className="text-muted-foreground">Art.Nr: {m.artikelnummer}</span>}
                                        {m.kosten > 0 && <span className="text-green-700 ml-auto">{m.kosten.toFixed(2)} €</span>}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div className="flex flex-col gap-1 flex-shrink-0">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-blue-600"
                                  onClick={() => editTaskInNewOrder(task.id)}
                                  disabled={isBeingEdited}
                                  title="Aufgabe bearbeiten"
                                  data-testid={`edit-pending-task-${task.id}`}
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive"
                                  onClick={() => removeTaskFromNewOrder(task.id)}
                                  title="Aufgabe entfernen"
                                  data-testid={`remove-pending-task-${task.id}`}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Add Task Form */}
                    <div className="space-y-3 pt-2 border-t">
                      <div className="space-y-2">
                        <Label className="text-sm">Aufgabenbeschreibung *</Label>
                        <div className="flex gap-2">
                          <Input
                            value={currentTask.description}
                            onChange={(e) => setCurrentTask({ ...currentTask, description: e.target.value })}
                            placeholder="z.B. Ölwechsel, Bremsbeläge wechseln..."
                            data-testid="new-task-description"
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            variant={isListening && activeField === 'taskDescription' ? "destructive" : "outline"}
                            size="icon"
                            onClick={voiceTaskDescription}
                            className="flex-shrink-0"
                            data-testid="voice-task-description"
                          >
                            {isListening && activeField === 'taskDescription' ? (
                              <MicOff className="w-4 h-4" />
                            ) : (
                              <Mic className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                      
                      <div className="space-y-1">
                        <Label className="text-xs">Zeit (min)</Label>
                        <Input
                          type="number"
                          min="0"
                          value={currentTask.time_minutes}
                          onChange={(e) => setCurrentTask({ ...currentTask, time_minutes: parseInt(e.target.value) || 0 })}
                          className="text-sm w-32"
                          data-testid="new-task-time"
                        />
                      </div>

                      {/* Materials for this task */}
                      <div className="bg-white p-3 rounded border space-y-2">
                        <Label className="text-xs font-medium flex items-center gap-1">
                          <Package className="w-3 h-3" />
                          Materialien für diese Aufgabe
                        </Label>
                        
                        {/* Added materials */}
                        {currentTask.materialien.length > 0 && (
                          <div className="space-y-1">
                            {currentTask.materialien.map((m, idx) => (
                              <div key={idx} className="text-xs bg-slate-100 p-2 rounded flex items-center gap-2">
                                <span className="font-medium">{m.bezeichnung}</span>
                                {(m.anzahl || m.menge) && (
                                  <span className="text-muted-foreground">
                                    ({m.anzahl || m.menge}{m.einheit ? ` ${m.einheit}` : ''})
                                  </span>
                                )}
                                {m.artikelnummer && <span className="text-muted-foreground">Art.Nr: {m.artikelnummer}</span>}
                                {m.kosten > 0 && <span className="text-green-700">{m.kosten.toFixed(2)} €</span>}
                                <div className="flex gap-1 ml-auto">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-5 w-5 text-blue-600"
                                    onClick={() => editMaterialInCurrentTask(idx)}
                                    title="Bearbeiten"
                                  >
                                    <Pencil className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-5 w-5 text-destructive"
                                    onClick={() => removeMaterialFromCurrentTask(idx)}
                                    title="Löschen"
                                  >
                                    <X className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Add material form with voice input */}
                        <div className="space-y-2">
                          <div className="flex gap-1 items-end">
                            <div className="flex-1 space-y-1">
                              <Label className="text-[10px]">Bezeichnung</Label>
                              <MaterialAutocomplete
                                value={currentMaterial.bezeichnung}
                                onChange={(val) => setCurrentMaterial({ ...currentMaterial, bezeichnung: val, material_id: "" })}
                                onSelect={(m) => setCurrentMaterial(materialFromInventory(m, currentMaterial))}
                                placeholder="Material suchen..."
                              />
                            </div>
                            <Button
                              type="button"
                              variant={isListening && activeField === 'materialBezeichnung' ? "destructive" : "ghost"}
                              size="icon"
                              className="h-8 w-8 flex-shrink-0"
                              onClick={() => voiceMaterialBezeichnung()}
                            >
                              <Mic className="w-3 h-3" />
                            </Button>
                          </div>
                          <MaterialFormFields material={currentMaterial} setMaterial={setCurrentMaterial} testIdPrefix="current-material" />
                          {currentMaterial.material_id && (
                            <MaterialUsageHistory
                              materialId={currentMaterial.material_id}
                              materialName={currentMaterial.bezeichnung}
                              defaultOpen={true}
                            />
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant={editingCurrentMaterialIndex >= 0 ? "default" : "secondary"}
                            size="sm"
                            className="flex-1 text-xs h-7"
                            onClick={addMaterialToCurrentTask}
                          >
                            {editingCurrentMaterialIndex >= 0 ? (
                              <>
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Material aktualisieren
                              </>
                            ) : (
                              <>
                                <Plus className="w-3 h-3 mr-1" />
                                Material hinzufügen
                              </>
                            )}
                          </Button>
                          {editingCurrentMaterialIndex >= 0 && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="text-xs h-7"
                              onClick={cancelCurrentMaterialEdit}
                            >
                              Abbrechen
                            </Button>
                          )}
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant={editingNewTaskId ? "default" : "outline"}
                          size="sm"
                          className="flex-1"
                          onClick={addTaskToNewOrder}
                          data-testid="add-task-to-order"
                        >
                          {editingNewTaskId ? (
                            <>
                              <CheckCircle2 className="w-4 h-4 mr-2" />
                              Aufgabe aktualisieren
                            </>
                          ) : (
                            <>
                              <Plus className="w-4 h-4 mr-2" />
                              Aufgabe hinzufügen
                            </>
                          )}
                        </Button>
                        {editingNewTaskId && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={cancelEditNewTask}
                            data-testid="cancel-edit-pending-task"
                          >
                            Abbrechen
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Edit mode - comprehensive form */}
                  {/* Section: Auftragsdaten */}
                  <div className="border-b pb-4">
                    <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider mb-3">Auftragsdaten</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Auftragsnummer</Label>
                        <div className="flex gap-2">
                          <Input
                            value={formData.auftragsnummer}
                            onChange={(e) => setFormData({ ...formData, auftragsnummer: e.target.value })}
                            placeholder="z.B. 2024-001"
                            data-testid="edit-order-number-input"
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            variant={isListening && activeField === 'auftragsnummer' ? "destructive" : "outline"}
                            size="icon"
                            onClick={() => startListening('auftragsnummer', (val) => setFormData(prev => ({ ...prev, auftragsnummer: val })), formData.auftragsnummer)}
                            className="flex-shrink-0"
                          >
                            <Mic className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Status</Label>
                        <Select
                          value={formData.status}
                          onValueChange={(value) => setFormData({ ...formData, status: value })}
                        >
                          <SelectTrigger data-testid="edit-order-status-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="offen">Offen</SelectItem>
                            <SelectItem value="in_bearbeitung">In Bearbeitung</SelectItem>
                            <SelectItem value="erledigt">Erledigt</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2 mt-4">
                      <Label>Beschreibung</Label>
                      <div className="flex gap-2">
                        <Textarea
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          rows={2}
                          data-testid="edit-order-description-input"
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant={isListening && activeField === 'description' ? "destructive" : "outline"}
                          size="icon"
                          onClick={() => startListening('description', (val) => setFormData(prev => ({ ...prev, description: val })), formData.description)}
                          className="flex-shrink-0 self-start"
                        >
                          <Mic className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Mitarbeiter-Zuweisung im Edit-Modus */}
                  {mitarbeiterList.length > 0 && (
                    <div className="border-b pb-4">
                      <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                        <UserCog className="w-4 h-4" />
                        Mitarbeiter-Zuweisung
                      </h3>
                      <div className="space-y-2">
                        {mitarbeiterList.map((ma) => (
                          <label key={ma.id} className="flex items-center gap-2 cursor-pointer text-sm" data-testid={`edit-assign-ma-${ma.id}`}>
                            <Checkbox
                              checked={formData.assigned_to.includes(ma.id)}
                              onCheckedChange={(checked) => {
                                setFormData(prev => ({
                                  ...prev,
                                  assigned_to: checked
                                    ? [...prev.assigned_to, ma.id]
                                    : prev.assigned_to.filter(id => id !== ma.id)
                                }));
                              }}
                            />
                            <span>{ma.name}</span>
                            <span className="text-muted-foreground text-xs">({ma.email})</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Section: Kundendaten */}
                  <div className="border-b pb-4">
                    <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Kundendaten
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Vorname</Label>
                        <div className="flex gap-2">
                          <Input
                            value={customerData.first_name}
                            onChange={(e) => setCustomerData({ ...customerData, first_name: e.target.value })}
                            data-testid="edit-customer-first-name"
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            variant={isListening && activeField === 'customer_first_name' ? "destructive" : "outline"}
                            size="icon"
                            onClick={() => startListening('customer_first_name', (val) => setCustomerData(prev => ({ ...prev, first_name: val })), customerData.first_name)}
                            className="flex-shrink-0"
                          >
                            <Mic className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Nachname</Label>
                        <div className="flex gap-2">
                          <Input
                            value={customerData.last_name}
                            onChange={(e) => setCustomerData({ ...customerData, last_name: e.target.value })}
                            data-testid="edit-customer-last-name"
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            variant={isListening && activeField === 'customer_last_name' ? "destructive" : "outline"}
                            size="icon"
                            onClick={() => startListening('customer_last_name', (val) => setCustomerData(prev => ({ ...prev, last_name: val })), customerData.last_name)}
                            className="flex-shrink-0"
                          >
                            <Mic className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Telefon</Label>
                        <div className="flex gap-2">
                          <Input
                            value={customerData.phone}
                            onChange={(e) => setCustomerData({ ...customerData, phone: e.target.value })}
                            data-testid="edit-customer-phone"
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            variant={isListening && activeField === 'customer_phone' ? "destructive" : "outline"}
                            size="icon"
                            onClick={() => startListening('customer_phone', (val) => setCustomerData(prev => ({ ...prev, phone: val })), customerData.phone)}
                            className="flex-shrink-0"
                          >
                            <Mic className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>E-Mail</Label>
                        <div className="flex gap-2">
                          <Input
                            value={customerData.email}
                            onChange={(e) => setCustomerData({ ...customerData, email: e.target.value })}
                            data-testid="edit-customer-email"
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            variant={isListening && activeField === 'customer_email' ? "destructive" : "outline"}
                            size="icon"
                            onClick={() => startListening('customer_email', (val) => setCustomerData(prev => ({ ...prev, email: val })), customerData.email)}
                            className="flex-shrink-0"
                          >
                            <Mic className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Straße</Label>
                        <div className="flex gap-2">
                          <Input
                            value={customerData.street}
                            onChange={(e) => setCustomerData({ ...customerData, street: e.target.value })}
                            data-testid="edit-customer-street"
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            variant={isListening && activeField === 'customer_street' ? "destructive" : "outline"}
                            size="icon"
                            onClick={() => startListening('customer_street', (val) => setCustomerData(prev => ({ ...prev, street: val })), customerData.street)}
                            className="flex-shrink-0"
                          >
                            <Mic className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-2">
                          <Label>PLZ</Label>
                          <Input
                            value={customerData.postal_code}
                            onChange={(e) => setCustomerData({ ...customerData, postal_code: e.target.value })}
                            data-testid="edit-customer-postal-code"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Stadt</Label>
                          <div className="flex gap-2">
                            <Input
                              value={customerData.city}
                              onChange={(e) => setCustomerData({ ...customerData, city: e.target.value })}
                              data-testid="edit-customer-city"
                              className="flex-1"
                            />
                            <Button
                              type="button"
                              variant={isListening && activeField === 'customer_city' ? "destructive" : "outline"}
                              size="icon"
                              onClick={() => startListening('customer_city', (val) => setCustomerData(prev => ({ ...prev, city: val })), customerData.city)}
                              className="flex-shrink-0"
                            >
                              <Mic className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section: Fahrzeugdaten */}
                  <div className="border-b pb-4">
                    <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                      <Car className="w-4 h-4" />
                      Fahrzeugdaten
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Kennzeichen</Label>
                        <div className="flex gap-2">
                          <Input
                            value={vehicleData.license_plate}
                            onChange={(e) => setVehicleData({ ...vehicleData, license_plate: e.target.value })}
                            data-testid="edit-vehicle-license-plate"
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            variant={isListening && activeField === 'vehicle_license_plate' ? "destructive" : "outline"}
                            size="icon"
                            onClick={() => startListening('vehicle_license_plate', (val) => setVehicleData(prev => ({ ...prev, license_plate: val })), vehicleData.license_plate)}
                            className="flex-shrink-0"
                          >
                            <Mic className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Hersteller</Label>
                        <div className="flex gap-2">
                          <Input
                            value={vehicleData.brand}
                            onChange={(e) => setVehicleData({ ...vehicleData, brand: e.target.value })}
                            data-testid="edit-vehicle-brand"
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            variant={isListening && activeField === 'vehicle_brand' ? "destructive" : "outline"}
                            size="icon"
                            onClick={() => startListening('vehicle_brand', (val) => setVehicleData(prev => ({ ...prev, brand: val })), vehicleData.brand)}
                            className="flex-shrink-0"
                          >
                            <Mic className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Modell</Label>
                        <div className="flex gap-2">
                          <Input
                            value={vehicleData.model}
                            onChange={(e) => setVehicleData({ ...vehicleData, model: e.target.value })}
                            data-testid="edit-vehicle-model"
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            variant={isListening && activeField === 'vehicle_model' ? "destructive" : "outline"}
                            size="icon"
                            onClick={() => startListening('vehicle_model', (val) => setVehicleData(prev => ({ ...prev, model: val })), vehicleData.model)}
                            className="flex-shrink-0"
                          >
                            <Mic className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Baujahr</Label>
                        <Input
                          type="number"
                          value={vehicleData.year}
                          onChange={(e) => setVehicleData({ ...vehicleData, year: e.target.value })}
                          data-testid="edit-vehicle-year"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Fahrgestellnummer (VIN)</Label>
                        <div className="flex gap-2">
                          <Input
                            value={vehicleData.vin}
                            onChange={(e) => setVehicleData({ ...vehicleData, vin: e.target.value })}
                            data-testid="edit-vehicle-vin"
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            variant={isListening && activeField === 'vehicle_vin' ? "destructive" : "outline"}
                            size="icon"
                            onClick={() => startListening('vehicle_vin', (val) => setVehicleData(prev => ({ ...prev, vin: val })), vehicleData.vin)}
                            className="flex-shrink-0"
                          >
                            <Mic className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Kilometerstand</Label>
                        <Input
                          type="number"
                          value={vehicleData.mileage}
                          onChange={(e) => setVehicleData({ ...vehicleData, mileage: e.target.value })}
                          data-testid="edit-vehicle-mileage"
                          placeholder="z.B. 125000"
                        />
                      </div>
                    </div>
                  </div>

                  <p className="text-xs text-muted-foreground">
                    💡 Aufgaben können Sie direkt im Auftrag über "Aufgaben anzeigen" bearbeiten.
                  </p>
                </>
              )}
              
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit" className="btn-primary" data-testid="order-submit-button">
                  {editingOrder ? "Speichern" : "Auftrag erstellen"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="order-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[180px]" data-testid="status-filter-select">
            <SelectValue placeholder="Status filtern" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="alle">Alle Status</SelectItem>
            <SelectItem value="offen">Offen</SelectItem>
            <SelectItem value="in_bearbeitung">In Bearbeitung</SelectItem>
            <SelectItem value="erledigt">Erledigt</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Orders List */}
      <div className="space-y-4" data-testid="orders-list">
        {filteredOrders.length > 0 ? (
          filteredOrders.map((order) => {
            const totals = calculateTotals(order.tasks || []);
            const isHighlighted = highlightedOrderId === order.id;
            return (
              <OrderCard
                key={order.id}
                order={order}
                totals={totals}
                isHighlighted={isHighlighted}
                user={user}
                actions={{
                  openOrderDetail,
                  handleStatusChange,
                  handleArchive,
                  handleUnarchive,
                  handleEdit,
                  handleDelete,
                  handleToggleTask,
                  openEditTaskDialog,
                  handleDeleteTask,
                  onAddTask: (o) => { setSelectedOrder(o); setTaskDialogOpen(true); },
                  startTimer,
                  togglePause,
                  stopTimer,
                  deleteTimeEntry,
                  openManualTimeEntry,
                  handleToggleArbeit,
                  handleAbschlusskontrolleChange,
                  getActiveTimeEntry,
                  navigate
                }}
              />
            );
          })
        ) : (
          <Card className="border shadow-sm">
            <CardContent className="text-center py-12 text-muted-foreground">
              <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>{searchTerm || statusFilter !== "alle" ? "Keine Aufträge gefunden" : "Noch keine Aufträge vorhanden"}</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Add Task Dialog */}
      <TaskFormDialog
        open={taskDialogOpen}
        onOpenChange={(v) => { setTaskDialogOpen(v); if (!v) setSelectedOrder(null); }}
        orderId={selectedOrder?.id}
        onSuccess={fetchData}
      />

      {/* Edit Task Dialog */}
      <TaskFormDialog
        open={editTaskDialogOpen}
        onOpenChange={(v) => { setEditTaskDialogOpen(v); if (!v) { setEditingTask(null); setSelectedOrder(null); } }}
        orderId={selectedOrder?.id}
        editTask={editingTask}
        onSuccess={fetchData}
      />

      {/* Order Detail Sheet */}
      {detailOrder && (
        <OrderDetailSheet
          order={detailOrder}
          open={detailSheetOpen}
          customers={customers}
          vehicles={vehicles}
          onOpenChange={(open) => {
            setDetailSheetOpen(open);
            if (!open) { setDetailOrder(null); fetchData(); }
          }}
          onOrderUpdated={(updatedOrder) => {
            setDetailOrder(updatedOrder);
            setOrders(prev => prev.map(o => o.id === updatedOrder.id ? updatedOrder : o));
          }}
        />
      )}

      {/* Manual Time Entry Dialog */}
      <ManualTimeDialog
        open={manualTimeOpen}
        onOpenChange={setManualTimeOpen}
        form={manualTimeForm}
        setForm={setManualTimeForm}
        onSubmit={submitManualTimeEntry}
      />

      {/* "Artikel speichern?" Prompt for new order materials */}
      {pendingMaterialForLager && !quickAddOpen && (
        <Dialog open={true} onOpenChange={() => setPendingMaterialForLager(null)}>
          <DialogContent className="sm:max-w-[440px]" data-testid="lager-prompt-dialog-new-order">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "Barlow Condensed" }}>Artikel speichern?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              <strong>"{pendingMaterialForLager.bezeichnung}"</strong> ist neu. Soll der Artikel gespeichert werden, damit er in Zukunft im Autocomplete vorgeschlagen wird?
            </p>
            <div className="mt-3 grid grid-cols-1 gap-2">
              <Button
                onClick={() => { setPendingMaterialForLagerMode("order_specific"); setQuickAddOpen(true); }}
                className="gap-2 justify-start"
                data-testid="save-as-order-specific-btn"
              >
                <ClipboardList className="w-4 h-4" />
                <div className="text-left">
                  <div className="font-medium">Nur für diesen Auftrag</div>
                  <div className="text-xs opacity-80 font-normal">Sonderbestellung, kein Lagerbestand</div>
                </div>
              </Button>
              <Button
                variant="outline"
                onClick={() => { setPendingMaterialForLagerMode("lager"); setQuickAddOpen(true); }}
                className="gap-2 justify-start"
                data-testid="save-as-lager-btn"
              >
                <Package className="w-4 h-4" />
                <div className="text-left">
                  <div className="font-medium">Als Lagerartikel</div>
                  <div className="text-xs text-muted-foreground font-normal">Mit Bestandsführung & Inventur</div>
                </div>
              </Button>
              <Button
                variant="ghost"
                onClick={() => setPendingMaterialForLager(null)}
                className="text-muted-foreground"
                data-testid="skip-save-material-btn"
              >
                Nicht speichern
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <QuickAddMaterialDialog
        open={quickAddOpen}
        onOpenChange={(v) => { setQuickAddOpen(v); if (!v) { setPendingMaterialForLager(null); setPendingMaterialForLagerMode(null); } }}
        prefill={pendingMaterialForLager}
        initialMode={pendingMaterialForLagerMode}
        onSaved={(saved) => {
          // Update the material in the current task with the new material_id and Lager values
          if (pendingMaterialForLager) {
            setCurrentTask(prev => ({
              ...prev,
              materialien: prev.materialien.map(m => {
                if (m.bezeichnung !== pendingMaterialForLager.bezeichnung || m.material_id) return m;
                const anzahl = parseFloat(m.anzahl) || 0;
                const einzelpreis = saved.verkaufspreis ?? m.einzelpreis ?? 0;
                return {
                  ...m,
                  material_id: saved.id,
                  // Adopt the bezeichnung that was actually saved to Lager (user may have
                  // refined it in the QuickAdd dialog).
                  bezeichnung: saved.bezeichnung || m.bezeichnung,
                  artikelnummer: saved.artikelnummer || m.artikelnummer || "",
                  einkaufspreis: saved.einkaufspreis ?? 0,
                  marge_prozent: saved.marge_prozent ?? 0,
                  einzelpreis,
                  kosten: parseFloat((anzahl * einzelpreis).toFixed(2)),
                  einheit: saved.einheit || m.einheit || "Stück",
                };
              })
            }));
          }
          setPendingMaterialForLager(null);
          setPendingMaterialForLagerMode(null);
        }}
      />
    </div>
  );
};

export default WorkOrdersPage;
